import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { GrowthCrmService, GrowthLeadResponse } from './growth-crm.service';

@Component({
  standalone: true,
  selector: 'ain-growth-lead-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    PartySelectorComponent,
    FormsModule,
    ButtonModule,
    SelectModule,
    InputTextModule,
    RouterLink
  ],
  template: `
    <ain-page [eyebrow]="tr('growth.eyebrow', 'رشد')" [title]="pageTitle()" [description]="tr('growth.crm.leads.description', 'جزئیات لید را مدیریت کنید.')">
      <div class="toolbar">
        <a pButton routerLink="/growth/crm/leads" severity="secondary" [label]="tr('common.back', 'بازگشت')"></a>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('growth.crm.leads.errorTitle')" [message]="error() ?? ''" />
      } @else {
        <section class="card">
          <div class="section-title">
            <h3>{{ isNew() ? t('growth.crm.leads.create') : t('growth.crm.leads.detailTitle') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.crm.leads.help.detail.title'"
              [descriptionKey]="'growth.crm.leads.help.detail.description'"
              [bulletKeys]="[
                'growth.crm.leads.help.detail.bullets.score',
                'growth.crm.leads.help.detail.bullets.handoff'
              ]" />
          </div>

          @if (isNew()) {
            <div class="grid">
              <ain-form-field [label]="t('growth.crm.leads.fields.party')">
                <ain-party-selector [(ngModel)]="createModel.partyId" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.crm.leads.fields.source')">
                <p-select [options]="sourceOptions" optionLabel="label" optionValue="value" [(ngModel)]="createModel.source" appendTo="body" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.crm.leads.fields.channel')">
                <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createModel.channelKey" appendTo="body" />
              </ain-form-field>
            </div>
            <div class="actions">
              <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="createLead()"></button>
            </div>
          } @else if (lead()) {
            <div class="detail-grid">
              <p><strong>{{ t('growth.crm.leads.columns.leadNumber') }}:</strong> {{ lead()!.leadNumber }}</p>
              <p><strong>{{ t('growth.crm.leads.columns.status') }}:</strong> {{ localizeStatus(lead()!.status) }}</p>
              <p><strong>{{ t('growth.crm.leads.columns.score') }}:</strong> {{ lead()!.score ?? '-' }}</p>
              <p><strong>{{ t('growth.crm.leads.fields.contentAsset') }}:</strong> {{ lead()!.contentAssetId ? t('growth.crm.leads.refs.linked') : '-' }}</p>
              <p><strong>{{ t('growth.crm.leads.fields.campaign') }}:</strong> {{ lead()!.campaignId ? t('growth.crm.leads.refs.linked') : '-' }}</p>
              <p><strong>{{ t('growth.crm.leads.fields.engagementEvent') }}:</strong> {{ lead()!.engagementEventId ? t('growth.crm.leads.refs.linked') : '-' }}</p>
            </div>
            <div class="actions">
              <button pButton type="button" [label]="t('growth.crm.leads.actions.qualify')" (click)="qualifyLead()"></button>
              <button pButton type="button" severity="danger" [label]="t('growth.crm.leads.actions.disqualify')" (click)="disqualifyLead()"></button>
            </div>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 12px; }
    .section-title { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .grid, .detail-grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .detail-grid p { margin: 0; }
    .actions { display: flex; flex-wrap: wrap; gap: 8px; }
  `]
})
export class GrowthLeadDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly crmService = inject(GrowthCrmService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly lead = signal<GrowthLeadResponse | null>(null);
  readonly isNew = signal(false);

  createModel = {
    partyId: null as string | null,
    source: 'form_submit',
    channelKey: 'web'
  };

  readonly sourceOptions = [
    { value: 'form_submit', labelKey: 'growth.crm.leads.source.formSubmit', label: '' },
    { value: 'request_demo', labelKey: 'growth.crm.leads.source.requestDemo', label: '' },
    { value: 'request_quote', labelKey: 'growth.crm.leads.source.requestQuote', label: '' },
    { value: 'social_comment', labelKey: 'growth.crm.leads.source.socialComment', label: '' },
    { value: 'social_dm', labelKey: 'growth.crm.leads.source.socialDm', label: '' },
    { value: 'manual', labelKey: 'growth.crm.leads.source.manual', label: '' }
  ];

  ngOnInit(): void {
    this.localizeOptions();
    const leadId = this.route.snapshot.paramMap.get('leadId');
    this.isNew.set(!leadId || leadId === 'new');
    if (this.isNew()) {
      this.loading.set(false);
      return;
    }

    this.crmService.getLead(leadId!).subscribe({
      next: item => {
        this.lead.set(item);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('growth.crm.leads.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  pageTitle(): string {
    return this.isNew()
      ? this.tr('growth.crm.leads.create', 'ایجاد لید')
      : this.tr('growth.crm.leads.detailTitle', 'جزئیات لید');
  }

  channelOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'web', label: this.t('growth.crm.leads.channel.web') },
      { value: 'email', label: this.t('growth.crm.leads.channel.email') },
      { value: 'social', label: this.t('growth.crm.leads.channel.social') },
      { value: 'landing', label: this.t('growth.crm.leads.channel.landing') }
    ];
  }

  createLead(): void {
    this.saving.set(true);
    this.crmService.createLead({
      source: this.createModel.source,
      channelKey: this.createModel.channelKey,
      partyId: this.createModel.partyId
    }).subscribe({
      next: created => this.router.navigate(['/growth/crm/leads', created.id]),
      error: () => {
        this.error.set(this.t('growth.crm.leads.errorCreate'));
        this.saving.set(false);
      }
    });
  }

  qualifyLead(): void {
    const current = this.lead();
    if (!current) return;
    this.crmService.qualifyLead(current.id).subscribe({ next: updated => this.lead.set(updated) });
  }

  disqualifyLead(): void {
    const current = this.lead();
    if (!current) return;
    this.crmService.disqualifyLead(current.id).subscribe({ next: updated => this.lead.set(updated) });
  }

  localizeStatus(status: string): string {
    const key = `growth.crm.leads.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  private localizeOptions(): void {
    for (const item of this.sourceOptions) {
      item.label = this.t(item.labelKey);
    }
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  tr(key: string, fallback: string): string {
    const translated = this.t(key);
    return translated === key ? fallback : translated;
  }
}
